import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { useCalfForm } from "../forms/calfFields";
import { filledRows, isGroupEmpty } from "./calfSections";
import { SectionCard, CardRow } from "./SectionCard";

/**
 * One card: a group of the calf's fields, read by default and edited in place.
 *
 * Editing is per card rather than per tab so no form is ever long. A tab can
 * then gather several subjects — the facts fixed at birth, say — without putting
 * fourteen fields in front of anyone at once.
 */
function CalfGroupCard({ group, calf, onSaved, canEdit }) {
  const [isEditing, setEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const form = useCalfForm();

  // Reload from the calf each time editing opens, so cancelling and reopening
  // always starts from what is actually stored.
  useEffect(() => {
    if (isEditing) form.load(calf);
    // form.load is a fresh closure each render, so listing it would loop; the
    // open/close edge and the calf itself are what actually matter here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, calf]);

  const rows = filledRows(group, calf);
  const isEmpty = isGroupEmpty(group, calf);

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);

    // The form was loaded from the whole calf, so fields outside this card go
    // back unchanged and only what was edited here actually differs.
    api
      .put("/calfs/" + calf.pk_calf, form.toPayload())
      .then(() => {
        setSaving(false);
        setEditing(false);
        onSaved();
      })
      .catch((err) => {
        console.error("There was an error saving the calf!", err);
        setError("L'enregistrement a échoué. Réessayez.");
        setSaving(false);
      });
  };

  if (isEditing) {
    const Fields = group.Fields;
    return (
      <SectionCard title={group.title}>
        <form onSubmit={handleSubmit} className="flex w-full flex-col p-4">
          <Fields form={form} />

          {error && (
            <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="w-1/2 rounded-lg border border-stone-300 bg-white py-3 font-medium text-stone-600 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="w-1/2 rounded-lg bg-amber-400 py-3 font-medium text-white disabled:opacity-50 dark:bg-amber-600"
            >
              {isSaving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title={group.title}
      action={
        canEdit ? (
          <button
            onClick={() => setEditing(true)}
            className="text-[15px] font-medium text-amber-500 dark:text-amber-400"
          >
            {isEmpty ? "Compléter" : "Modifier"}
          </button>
        ) : null
      }
    >
      {isEmpty ? (
        <p className="px-4 py-4 text-sm text-stone-400 dark:text-slate-400">
          Non renseigné
        </p>
      ) : (
        rows.map(([label, value], index) => (
          <CardRow
            key={label}
            label={label}
            value={value}
            last={index === rows.length - 1}
          />
        ))
      )}
    </SectionCard>
  );
}

/** One tab of the calf's own data: its cards, plus anything it manages itself. */
function CalfInfoTab({ section, calf, onSaved, canEdit = true }) {
  const Extra = section.Extra;

  return (
    <div className="flex w-full flex-col md:max-w-2xl">
      {section.groups.map((group) => (
        <CalfGroupCard
          key={group.id}
          group={group}
          calf={calf}
          onSaved={onSaved}
          canEdit={canEdit}
        />
      ))}

      {Extra && <Extra calf={calf} canEdit={canEdit} />}
    </div>
  );
}

export default CalfInfoTab;
